import React, { useEffect, useRef, useState } from 'react';
import { Modal } from './comps';
import styled from 'styled-components';
import { FormikProvider, useFormik } from 'formik';
import { useScenario } from 'src/state/scenario';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { useParams } from 'react-router';
import { Guard } from './Guard';
import { getDiff } from 'src/utils/getDiff';
import { Poppins, ScenarioModalSkeleton, Spacer } from 'src/common';
import ScenarioForm from '../formComposed/ScenarioForm';
import { scenarioValidationSchema } from '../formComposed/ScenarioForm/util';
import { selectUserOption } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import _ from 'lodash';
import { formatExpression } from '../FormulaInput/util';
import { KeyType, isQueryLoading, useCache, useScenarioGroups } from 'src/graphql';
import {
  ScenarioInput,
  useAddScenarioToGroupMutation,
  useScenarioQuery,
  useUpsertScenarioMutation,
} from 'src/graphql/_generated';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
const StyledModal = styled(Modal)`
  width: 950px;
  color: ${({ theme }) => theme.colors.cflowerBlue};
  padding: 0 !important;
  overflow: hidden;
  max-height: calc(100% - 40px);
  overflow-y: auto;
`;

const ErrorLabel = styled(Poppins)`
  margin-top: 10px;
  text-align: center;
  width: 100%;
  color: ${({ theme }) => theme.colors.error};
  font-weight: 400;
`;

interface ScenarioModalProps {
  onClose: () => void;
  invalidateQueries: KeyType[];
}

export const ScenarioModal: React.FC<ScenarioModalProps> = ({ onClose, invalidateQueries }) => {
  const [responseErr, setResponseErr] = useState('');
  const { id, closeModal } = useScenario();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const { usersOptios, workspaceId } = useAuth();
  const { refetchQuery } = useCache();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const initialGroupIdRef = useRef<string | null>(null);

  const {
    data: { scenario: scenarioGql } = {},
    loading: isLoadingScenarioGql,
    networkStatus,
  } = useScenarioQuery({ variables: { id: id! }, skip: !id });
  const isLoading = isQueryLoading({ networkStatus, data: scenarioGql, loading: isLoadingScenarioGql });

  const {
    data: scenarioGroupsData,
    loading: isLoadingGroups,
    networkStatus: scenarioGroupsNetworkStatus,
  } = useScenarioGroups(workspaceId);
  const scenarioGroups = scenarioGroupsData?.scenarioGroups || [];
  const isLoadingScenarioGroups = isQueryLoading({
    data: scenarioGroupsData,
    loading: isLoadingGroups,
    networkStatus: scenarioGroupsNetworkStatus,
  });
  const groupDirty = selectedGroupId !== initialGroupIdRef.current;

  const onSuccess = async () => {
    queryCache.invalidateQueries([QKeys.Audit]);
    await refetchQuery({ include: invalidateQueries });
    showSuccessToast(id ? toastMessages.scenario.updated : toastMessages.scenario.created);
    closeModal();
  };

  const [upsertScenario] = useUpsertScenarioMutation({
    onError: (err: Error) => setResponseErr(err?.message || 'Server error'),
  });
  const [addScenarioToGroup] = useAddScenarioToGroupMutation({
    onError: (err: Error) => setResponseErr(err?.message || 'Server error'),
  });

  const initialValues = {
    name: scenarioGql?.name,
    source: scenarioGql?.source,
    event: scenarioGql?.event,
    consequence: scenarioGql?.consequence,
    riskOwner: scenarioGql?.riskOwner || { text: '' },
    riskNarrative: scenarioGql?.riskNarrative,
    frequencyTimes: scenarioGql?.baseRisk?.frequencyTimes,
    frequencyYears: scenarioGql?.baseRisk?.frequencyYears,
    frequencyNote: scenarioGql?.frequencyNote,
    upper: scenarioGql?.baseRisk?.upper,
    lower: scenarioGql?.baseRisk?.lower,
    upperFormula: scenarioGql?.upperFormula,
    lowerFormula: scenarioGql?.lowerFormula,
    consequenceNote: scenarioGql?.consequenceNote,
    riskIdentifiedDate: scenarioGql?.riskIdentifiedDate,
    reviewDate: scenarioGql?.reviewDate,
    status: scenarioGql?.status || 'Draft',
  } satisfies ScenarioInput;

  const formik = useFormik({
    initialValues,
    validationSchema: scenarioValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setResponseErr('');
      setIsSaving(true);
      const lowerFormula = formatExpression(values.lowerFormula);
      const upperFormula = formatExpression(values.upperFormula);

      const { userAdd, userUpdate } = selectUserOption({
        usersOptios,
        selectedUser: values.riskOwner,
        initialUser: initialValues.riskOwner,
      });

      try {
        if (id) {
          const diff = getDiff(initialValues, { ...values, lowerFormula, upperFormula });
          const updateFields = {
            ...diff,
            riskOwner: userUpdate,
          };

          const res = await upsertScenario({
            variables: { assessmentId, scenarioData: { id, ...updateFields } },
          });
          const scenarioId = res.data?.upsertScenario?.id || id;
          if (scenarioId && groupDirty) {
            await addScenarioToGroup({ variables: { groupId: selectedGroupId ?? null, scenarioId } });
          }
          mpEvent(MPEvents.ScenarioUpdate, { id, updatedFields: _.keys(updateFields), tags: ['SCENARIO'] });
        } else {
          const res = await upsertScenario({
            variables: {
              assessmentId,
              scenarioData: {
                ...values,
                lowerFormula,
                upperFormula,
                riskOwner: userAdd,
              },
            },
          });
          const scenarioId = res.data?.upsertScenario?.id;
          if (scenarioId && groupDirty) {
            await addScenarioToGroup({ variables: { groupId: selectedGroupId ?? null, scenarioId } });
          }
          if (scenarioId) {
            mpEvent(MPEvents.ScenarioCreate, { id: scenarioId, tags: ['SCENARIO'] });
          }
        }
        await onSuccess();
      } catch (err: any) {
        setIsSaving(false);
        setResponseErr(err?.message || 'Server error');
      }
    },
  });

  useEffect(() => {
    if (!scenarioGql) return;
    if (!scenarioGroups.length) {
      setSelectedGroupId(null);
      if (initialGroupIdRef.current === null) {
        initialGroupIdRef.current = null;
      }
      return;
    }

    const matchedGroup = scenarioGroups.find((g) => g.scenarios.some((s) => s.id === scenarioGql.id));
    const matchedId = matchedGroup?.id ?? null;

    setSelectedGroupId(matchedId);
    if (initialGroupIdRef.current === null) {
      initialGroupIdRef.current = matchedId;
    }
  }, [scenarioGql, scenarioGroups]);

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={formik.dirty}
      modalComponent={
        <StyledModal isOpen onRequestClose={() => setIsCloseRequested(true)} persistent={isSaving}>
          <Spacer $px={30} />
          {!isLoading ? (
            <>
              <div className="h-padding">
                <Poppins className="m-title" px={28}>
                  {id ? 'Edit Scenario' : 'Add Scenario'}
                </Poppins>
                <Spacer $px={20} />
              </div>
              <FormikProvider value={formik}>
                <ScenarioForm
                  buttonTitle={id ? 'UPDATE' : 'CREATE'}
                  assessmentId={assessmentId}
                  scenarioGroups={scenarioGroups}
                  selectedGroupId={selectedGroupId}
                  onSelectGroup={setSelectedGroupId}
                  isLoadingScenarioGroups={isLoadingScenarioGroups}
                  groupDirty={groupDirty}
                  isSaving={isSaving}
                />
              </FormikProvider>
              {!!responseErr && (
                <div style={{ display: 'flex' }}>
                  <ErrorLabel px={14}>{responseErr}</ErrorLabel>
                </div>
              )}
              <Spacer $px={30} />
            </>
          ) : (
            <div className="h-padding">
              <ScenarioModalSkeleton />
              <Spacer $px={30} />
            </div>
          )}
        </StyledModal>
      }
    />
  );
};
